import { type Component, type JSX, Show, createEffect, onCleanup } from "solid-js";
import { Check, Trash2, X } from "lucide-solid";

export type ModalSize = "sm" | "md" | "lg" | "xl";

const SIZE_CLASSES: Record<ModalSize, string> = {
  sm: "max-w-md",   // 448px — подтверждения, 1–2 поля
  md: "max-w-2xl",  // 672px — стандартные формы
  lg: "max-w-4xl",  // 896px — мастера с двумя колонками
  xl: "max-w-6xl",  // 1152px — экраны-редакторы
};

export interface ModalShellProps {
  open: boolean;
  /** Esc + клик по X. Клик по backdrop НЕ закрывает (защита от потери данных). */
  onClose: () => void;
  title?: JSX.Element | string;
  size?: ModalSize;
  /** "top" поднимает модалку к верху (pt-20) — для боковых форм типа CreateUser/Rename. */
  align?: "center" | "top";
  /** Доп. классы для card-контейнера (например, `!p-0 flex flex-col h-[80vh]`). */
  containerClass?: string;

  /** Если задан — рисует Check (или кастомную saveIcon) слева от X. */
  onSave?: () => void;
  saveDisabled?: boolean;
  savePending?: boolean;
  saveLabel?: string;
  saveIcon?: JSX.Element;
  /** "danger" — для деструктивных submit (передача владения, разрыв связи). */
  saveTone?: "success" | "danger";

  /** Отдельная деструктивная кнопка (Trash2 слева от save/cancel).
   *  Используется, когда у модалки и save, и delete (см. EditIntervalModal). */
  onDelete?: () => void;
  deleteDisabled?: boolean;
  deletePending?: boolean;
  deleteLabel?: string;
  deleteIcon?: JSX.Element;

  children: JSX.Element;
}

/** Стандартная обёртка модалки по дизайн-системе [`06 Modals`](docs/design/ritm-flow-ds.pen).
 *
 *  - Backdrop `bg-slate-900/55`, клик по нему НЕ закрывает модалку.
 *  - `Esc` = `onClose`. `Enter` не используется (конфликт с textarea).
 *  - Иконки в правом верхнем углу: опционально `Trash2` (`text-danger`) + опц. `Check` (`text-success`) + всегда `X` (`text-danger`).
 *  - Размеры sm/md/lg/xl (см. `SIZE_CLASSES`).
 *
 *  Не подходит для модалок с собственным flex-column-scroll layout
 *  (StructureEditorModal, OperatingHoursCard, LocationMapPicker) — для них
 *  передавай `containerClass="!p-0 ..."` и рендери собственный header/body/footer
 *  под action-иконками. */
export const ModalShell: Component<ModalShellProps> = (props) => {
  createEffect(() => {
    if (!props.open) return;
    const handler = (e: KeyboardEvent) => {
      if (e.key === "Escape") props.onClose();
    };
    document.addEventListener("keydown", handler);
    onCleanup(() => document.removeEventListener("keydown", handler));
  });

  // Если у заголовка нет save/delete — оставляем место под одну X (pr-12),
  // иначе под 2–3 иконки (pr-24 / pr-32).
  const titlePadding = () => {
    let count = 1; // X всегда есть
    if (props.onSave) count++;
    if (props.onDelete) count++;
    return count === 1 ? "pr-12" : count === 2 ? "pr-24" : "pr-32";
  };

  return (
    <Show when={props.open}>
      <div
        class="fixed inset-0 z-50 flex justify-center bg-slate-900/55 p-4"
        classList={{
          "items-start pt-20": props.align === "top",
          "items-center": props.align !== "top",
        }}
      >
        <div
          class={`relative w-full rounded-lg border border-border bg-surface p-5 shadow-lg ${SIZE_CLASSES[props.size ?? "md"]}${props.containerClass ? ` ${props.containerClass}` : ""}`}
        >
          <div class="absolute right-3 top-3 z-10 flex items-center gap-1">
            <Show when={props.onDelete}>
              <button
                type="button"
                onClick={() => props.onDelete?.()}
                disabled={props.deleteDisabled || props.deletePending}
                aria-label={props.deleteLabel ?? "Удалить"}
                title={props.deleteLabel ?? "Удалить"}
                class="flex h-8 w-8 items-center justify-center rounded-md bg-transparent text-danger transition-colors hover:bg-surface-hover disabled:opacity-60"
              >
                <Show when={props.deletePending} fallback={props.deleteIcon ?? <Trash2 size={18} />}>
                  <span class="text-2xs">…</span>
                </Show>
              </button>
            </Show>
            <Show when={props.onSave}>
              <button
                type="button"
                onClick={() => props.onSave?.()}
                disabled={props.saveDisabled || props.savePending}
                aria-label={props.saveLabel ?? "Сохранить"}
                title={props.saveLabel ?? "Сохранить"}
                class="flex h-8 w-8 items-center justify-center rounded-md bg-transparent transition-colors hover:bg-surface-hover disabled:opacity-60"
                classList={{
                  "text-success": (props.saveTone ?? "success") === "success",
                  "text-danger": props.saveTone === "danger",
                }}
              >
                <Show when={props.savePending} fallback={props.saveIcon ?? <Check size={18} />}>
                  <span class="text-2xs">…</span>
                </Show>
              </button>
            </Show>
            <button
              type="button"
              onClick={props.onClose}
              disabled={props.savePending || props.deletePending}
              aria-label="Отменить"
              title="Отменить"
              class="flex h-8 w-8 items-center justify-center rounded-md bg-transparent text-danger transition-colors hover:bg-surface-hover disabled:opacity-60"
            >
              <X size={18} />
            </button>
          </div>

          <Show when={props.title}>
            <h2
              class="mb-4 text-xl font-semibold text-ink"
              classList={{ [titlePadding()]: true }}
            >
              {props.title}
            </h2>
          </Show>

          {props.children}
        </div>
      </div>
    </Show>
  );
};

export default ModalShell;
